import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {handleGetAllTextEntryService} from '../services/textEntry';

type Lang = 'en' | 'heb' | string;

type TextEntryItem = {
  key: string;
  value: string;
};

type GetAllTextEntryResponse = {
  success: boolean;
  data?: TextEntryItem[];
  message?: string;
};

type TextEntryMap = Record<string, string>;

type TextEntryContextValue = {
  allTextEntriesData: TextEntryMap;
  loadingTextEntriesData: boolean;
  lang: Lang;
  setLang: React.Dispatch<React.SetStateAction<Lang>>;
  refetchTextData: boolean;
  setRefetchTextData: React.Dispatch<React.SetStateAction<boolean>>;
  fetchAllTextEntries: (selectedLang: Lang) => Promise<void>;
};

const TextEntryContext = createContext<TextEntryContextValue | undefined>(
  undefined,
);

type ProviderProps = {
  children: ReactNode;
};

function TextEntryContextProvider({children}: ProviderProps) {
  const [allTextEntriesData, setAllTextEntriesData] = useState<TextEntryMap>(
    {},
  );
  const [loadingTextEntriesData, setLoadingTextEntriesData] = useState(false);
  const [lang, setLang] = useState<Lang>(
    (localStorage.getItem('currentLang') as Lang) ?? 'heb',
  );
  const [refetchTextData, setRefetchTextData] = useState(false);

  const fetchAllTextEntries = useCallback(async (selectedLang: Lang) => {
    setLoadingTextEntriesData(true);
    try {
      // If your service signature is already typed, this cast isn't needed.
      const response = (await handleGetAllTextEntryService(
        selectedLang,
      )) as GetAllTextEntryResponse;

      if (response.success) {
        const textObj = (response.data ?? []).reduce<TextEntryMap>(
          (acc, curr) => {
            acc[curr.key] = curr.value;
            return acc;
          },
          {},
        );
        setAllTextEntriesData(textObj);
      } else {
        alert(response.message || 'Error');
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong';
      alert(message);
    } finally {
      setLoadingTextEntriesData(false);
    }
  }, []);

  useEffect(() => {
    fetchAllTextEntries(lang);
  }, [lang, refetchTextData, fetchAllTextEntries]);

  const value = useMemo<TextEntryContextValue>(
    () => ({
      allTextEntriesData,
      loadingTextEntriesData,
      lang,
      setLang,
      refetchTextData,
      setRefetchTextData,
      fetchAllTextEntries,
    }),
    [
      allTextEntriesData,
      loadingTextEntriesData,
      lang,
      refetchTextData,
      fetchAllTextEntries,
    ],
  );

  return (
    <TextEntryContext.Provider value={value}>
      {children}
    </TextEntryContext.Provider>
  );
}

export default TextEntryContextProvider;

export const useTextEntryContext = (): TextEntryContextValue => {
  const ctx = useContext(TextEntryContext);
  if (!ctx) {
    throw new Error(
      'useTextEntryContext must be used within a TextEntryContextProvider',
    );
  }
  return ctx;
};
